import { Form, Link } from '@remix-run/react';
import { MagnifyingGlassIcon, Bars3Icon } from '@heroicons/react/24/outline';
import Logo from '~/assets/Logo';
import { useRootLoader } from '~/utils/use-root-loader';
import { useTranslation } from 'react-i18next';
import Cart from '~/assets/icons/Cart';
import { SubHeader } from './Sub-header';
import { useState } from 'react';

export function Header({
  onCartIconClick,
  cartQuantity,
}: {
  onCartIconClick: () => void;
  cartQuantity: number;
}) {
  const data = useRootLoader();
  const isSignedIn = !!data.activeCustomer.activeCustomer?.id;
  const { t } = useTranslation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  let initialQuery = '';
  if (typeof window !== 'undefined') {
    initialQuery = new URL(window.location.href).searchParams.get('q') ?? '';
  }

  return (
    <header className="sticky top-0 z-50 w-full">
      <div className="bg-primary text-white text-sm text-center py-3">
        Warning: Vaping Products Contain Nicotine - A Highly Addictive Chemical
      </div>

      {/* Upper nav bar */}
      <div className="bg-white shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-20 gap-4 md:gap-8">
            {/* Mobile Menu Icon */}
            <button
              className="md:hidden p-2 rounded-md text-gray-700 hover:text-black"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Open mobile menu"
            >
              <Bars3Icon className="h-6 w-6" />
            </button>

            {/* Logo */}
            <div className="hidden md:flex items-center shrink-0">
              <Link to="/" className="flex items-center">
                <Logo className="h-16 w-auto object-contain" />
              </Link>
            </div>

            {/* Search Bar - Full width on mobile */}
            <div className="flex-1 md:flex-1 mx-2 md:mx-0">
              <Form
                method="get"
                action="/search"
                key={initialQuery}
                className="relative w-full"
              >
                <input
                  type="search"
                  name="q"
                  defaultValue={initialQuery}
                  placeholder="What are you looking for?"
                  className="w-full pl-4 pr-10 py-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500 text-sm"
                />
                <button className="absolute right-1 top-1/2 -translate-y-1/2 hover:bg-primary-200/20 p-2 rounded-lg">
                  <MagnifyingGlassIcon className="h-5 w-5 font-bold text-gray-400" />
                </button>
              </Form>
            </div>

            {/* Cart and User */}
            <div className="flex items-center space-x-2 md:space-x-4">
              {/* Cart */}
              <button
                className="relative h-10 text-gray-200 gap-2 rounded flex items-center justify-center"
                onClick={onCartIconClick}
                aria-label="Open cart tray"
              >
                <Cart className="w-8 h-8 md:w-10 md:h-8" />
                <span className="hidden md:inline text-black text-sm">
                  Cart
                </span>
                {cartQuantity ? (
                  <div className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
                    {cartQuantity}
                  </div>
                ) : null}
              </button>

              {/* User - Desktop */}
              <Link
                to={isSignedIn ? '/account' : '/sign-in'}
                className="hidden md:flex items-center border px-4 py-2 rounded-lg space-x-1 text-gray-700 hover:text-black"
              >
                {isSignedIn ? t('account.myAccount') : t('account.signIn')}
              </Link>

              {/* Sign In - Mobile */}
              {!isSignedIn && (
                <Link
                  to="/sign-in"
                  className="md:hidden px-3 py-1 bg-white text-gray-700 border-2 rounded-md text-sm"
                >
                  Sign In
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white shadow-lg py-4">
          <div className="container mx-auto px-4 flex flex-col space-y-3">
            <Link
              to="/about-us"
              className="block text-base text-gray-700 hover:text-black"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              About Us
            </Link>
            {data.collections.slice(0, 3).map((collection) => (
              <Link
                to={`/collections/${collection.slug}`}
                key={collection.id}
                className="block text-base text-gray-700 hover:text-black"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {collection.name}
              </Link>
            ))}
            {isSignedIn && (
              <Link
                to="/account"
                className="block text-base text-gray-700 hover:text-black"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t('account.myAccount')}
              </Link>
            )}
          </div>
        </div>
      )}

      <SubHeader />
    </header>
  );
}
